import type { DocPageContent } from '../types';

/**
 * "How it learns your product" — the discovery page, and the one that was
 * missing entirely.
 *
 * ## Why it exists
 *
 * Every path through these docs used to start at `npx syncanix init`: point the
 * CLI at a repository, get a catalog. That is a real and precise mechanism, and
 * it is the wrong first sentence — it asks for engineering time and repository
 * access before the reader has seen anything work, and it is not what most
 * customers actually do. The widget reads the product on its own as people use
 * it, and a guided walk gets a first pass done in about an hour without anyone
 * opening an editor.
 *
 * So the primary path is the embed, this page is what happens next, and the
 * code scan keeps its whole section as the higher-precision option for a team
 * that wants one.
 *
 * ## What is deliberately not here
 *
 * No internal mechanism names, no flag names, no architecture. A reader needs to
 * know what is read, what is not, where it goes, and what they have to do —
 * which of those a customer can see is the whole test for whether it belongs on
 * this page.
 */
export const DISCOVERY_PAGE: DocPageContent = {
  slug: 'discovery',
  titleKey: 'docs.discovery.title',
  descriptionKey: 'docs.discovery.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.discovery.lead' },

    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.discovery.nothingOn.title',
      textKey: 'docs.discovery.nothingOn.body',
    },

    // ── The two ways it reads ──────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'two-ways', textKey: 'docs.discovery.twoWays.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.twoWays.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.discovery.twoWays.ambient.term',
          descKey: 'docs.discovery.twoWays.ambient.desc',
        },
        {
          termKey: 'docs.discovery.twoWays.walk.term',
          descKey: 'docs.discovery.twoWays.walk.desc',
        },
      ],
    },

    // ── The guided walk ────────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'walk', textKey: 'docs.discovery.walk.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.walk.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.discovery.walk.step.start.title',
          textKey: 'docs.discovery.walk.step.start.text',
        },
        {
          titleKey: 'docs.discovery.walk.step.click.title',
          textKey: 'docs.discovery.walk.step.click.text',
        },
        {
          titleKey: 'docs.discovery.walk.step.stop.title',
          textKey: 'docs.discovery.walk.step.stop.text',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.discovery.walk.browser.title',
      textKey: 'docs.discovery.walk.browser.body',
    },

    // ── What it reads ──────────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'reads', textKey: 'docs.discovery.reads.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.reads.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.discovery.reads.item.forms',
        'docs.discovery.reads.item.routes',
        'docs.discovery.reads.item.links',
        'docs.discovery.reads.item.structured',
      ],
    },

    // ── What it does not read ──────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'excluded', textKey: 'docs.discovery.excluded.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.excluded.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.discovery.excluded.item.secrets',
        'docs.discovery.excluded.item.values',
        'docs.discovery.excluded.item.thirdParty',
        'docs.discovery.excluded.item.blocked',
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.discovery.excluded.mask.title',
      textKey: 'docs.discovery.excluded.mask.body',
      links: {
        config: {
          target: { docSlug: 'widget/configuration' },
          labelKey: 'docs.discovery.link.config',
        },
      },
    },

    // ── Reviewing what it found ────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'review', textKey: 'docs.discovery.review.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.review.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.discovery.review.item.disabled',
        'docs.discovery.review.item.plain',
        'docs.discovery.review.item.confirms',
        'docs.discovery.review.item.risk',
      ],
    },

    // ── Keeping up ─────────────────────────────────────────────────────
    { kind: 'heading', level: 2, id: 'keeping-up', textKey: 'docs.discovery.keepingUp.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.keepingUp.body' },

    // ── When to add the code scan ──────────────────────────────────────
    { kind: 'heading', level: 2, id: 'code-scan', textKey: 'docs.discovery.codeScan.heading' },
    { kind: 'paragraph', textKey: 'docs.discovery.codeScan.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.discovery.codeScan.item.exact',
        'docs.discovery.codeScan.item.unreached',
        'docs.discovery.codeScan.item.drift',
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.discovery.codeScan.optional.title',
      textKey: 'docs.discovery.codeScan.optional.body',
      links: {
        cli: { target: { docSlug: 'cli' }, labelKey: 'docs.discovery.link.cli' },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.discovery.next.cli.title',
          textKey: 'docs.discovery.next.cli.text',
          to: { docSlug: 'cli' },
        },
        {
          titleKey: 'docs.discovery.next.catalog.title',
          textKey: 'docs.discovery.next.catalog.text',
          to: { docSlug: 'dashboard/catalog' },
        },
        {
          titleKey: 'docs.discovery.next.privacy.title',
          textKey: 'docs.discovery.next.privacy.text',
          to: { docSlug: 'trust/privacy' },
        },
      ],
    },
  ],
};
